import { BookOpen, Check, Copy, PenLine } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { CopyDraft, CopyPiece } from "@/lib/api";
import { logger } from "@/lib/logger";

import { AUDIENCE_PHRASE, LANGUAGE_NAMES } from "./utils";

interface CopyDraftPanelProps {
  draft: CopyDraft;
  /**
   * Where the panel is being read.
   *
   * In the thread it is the answer to what was just asked. Opened off the rail
   * it is a stored file — possibly an older version of a draft that has since
   * been rewritten — so the sub-line must not imply these words were written
   * just now. One component, because two spellings of this panel would drift.
   */
  context?: "thread" | "file";
}

/**
 * The written copy itself, as something to read and take away.
 *
 * Every other route in the composer ends in a file — a deck, a translated
 * document, a set of rendered ads — and the panel's job there is to describe
 * what was built. Here the words *are* the deliverable: the Word file is a
 * convenience for forwarding, and the thing a market actually does with this
 * screen is read a piece and lift it into LinkedIn.
 *
 * So the body is given the room of prose rather than the treatment of data:
 * full width, no truncation, no monospace, and its line breaks kept, because a
 * post's paragraph breaks are part of the copy. Everything that is *about* the
 * copy — where the facts came from, what the writer chose — sits below a rule
 * in smaller, fainter type, so that nobody skim-copying can mistake a note for
 * a line they are meant to publish.
 */
export function CopyDraftPanel({ draft, context = "thread" }: CopyDraftPanelProps) {
  const pieces = draft.pieces ?? [];

  // A draft can arrive with pieces missing — the writer drops anything it
  // could only finish by inventing a figure — and in the limit it drops them
  // all. The turn's own message already names what went and why, so an empty
  // panel would be a second, emptier version of that answer.
  if (pieces.length === 0) {
    return null;
  }

  const language = LANGUAGE_NAMES[draft.language] ?? draft.language;
  const audience = draft.audience ? (AUDIENCE_PHRASE[draft.audience] ?? draft.audience) : undefined;

  return (
    <section className="border-border bg-surface-muted/60 space-y-4 rounded-lg border p-4">
      <header className="space-y-1">
        <h3 className="font-heading text-foreground text-sm font-bold">
          {pieces.length} piece{pieces.length === 1 ? "" : "s"} of copy, in {language}
        </h3>
        <p className="text-muted text-xs leading-5">
          {audience ? `Written for ${audience}, in the VELUX tone of voice. ` : ""}
          {context === "file"
            ? // Read off the rail this is a saved file, and a later round may
              // already have replaced it. "These are the words themselves"
              // would quietly present an old version as the current answer.
              "These are the words in this file, as they were written — "
            : "These are the words themselves — "}
          copy a piece straight out and it lands with its line breaks intact and nothing else
          attached.
        </p>
      </header>

      {/* One per row rather than a grid: copy is prose, and prose read across
          two narrow columns is harder to judge than the same words read down
          one. It also means nothing has to reflow on a phone. */}
      <div className="space-y-3">
        {pieces.map((piece) => (
          <CopyPieceCard key={piece.key} piece={piece} />
        ))}
      </div>
    </section>
  );
}

/** One finished piece: its name, its words, its own copy button, its provenance. */
function CopyPieceCard({ piece }: { piece: CopyPiece }) {
  return (
    <article className="border-border bg-card space-y-2.5 rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-heading text-foreground text-sm font-bold">{piece.label}</h4>
        <CopyPieceButton label={piece.label} body={piece.body} />
      </div>

      {/* The copy, and only the copy. `whitespace-pre-wrap` is load-bearing:
          the blank lines a writer put between paragraphs are the shape of the
          post, and collapsing them would change what is being approved. */}
      <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
        {piece.body}
      </p>

      {/* Everything below the rule is the writer talking about the copy, not
          copy. The rule, the smaller type and the icons are what stop a note
          being pasted into LinkedIn by someone reading quickly. */}
      <div className="border-border/70 text-faint space-y-1 border-t pt-2 text-[11px] leading-5">
        <p className="flex items-start gap-1.5">
          <BookOpen className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            {/* A source is only required where the copy claims something.
                Saying so is more honest than a blank line that reads as a
                missing citation — same rule the ad copy states. */}
            {piece.source
              ? `Drawn from ${piece.source}.`
              : "Brand copy — it makes no factual claim, so there is no document behind it."}
          </span>
        </p>
        {piece.notes && (
          <p className="flex items-start gap-1.5">
            <PenLine className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              <span className="font-semibold">Why it reads this way: </span>
              {piece.notes}
            </span>
          </p>
        )}
      </div>
    </article>
  );
}

/**
 * Take this piece, and say so.
 *
 * The most-used control on the surface, so it confirms itself rather than
 * leaving someone to check the clipboard: the label changes, the button fills
 * in, and the change is announced for anyone not watching it. It copies the
 * body exactly — not the label, not the source, not the note — because what
 * gets pasted is what gets published.
 */
function CopyPieceButton({ label, body }: { label: string; body: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      logger.debug("[composer] copied piece", label);
    } catch (error) {
      logger.error("Could not copy the piece", error);
    }
  };

  return (
    <Button
      variant={copied ? "secondary" : "outline"}
      size="sm"
      onClick={copy}
      aria-label={`Copy the ${label.toLowerCase()}`}
    >
      {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
      <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
    </Button>
  );
}
